import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Receipt, Search } from "lucide-react";
import { useInvoices, useMarkInvoicePaid } from "@/api/hooks";
import { type InvoiceStatus } from "@/api/portalApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// ── Badge ─────────────────────────────────────────────────────────────────────

function InvoiceStatusBadge({ status }: { status: InvoiceStatus }) {
    const styles: Record<InvoiceStatus, string> = {
        PendingPayment: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
        Paid: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
        Cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
    };
    const labels: Record<InvoiceStatus, string> = {
        PendingPayment: "Pending Payment",
        Paid: "Paid",
        Cancelled: "Cancelled"
    };
    return (
        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", styles[status])}>
            {labels[status]}
        </span>
    );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(value: string | null | undefined) {
    if (!value) return "—";
    return new Date(value).toLocaleDateString(undefined, { dateStyle: "medium" });
}

function formatCurrency(amount: number, currency = "USD") {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
}

// ── Filter tabs ───────────────────────────────────────────────────────────────

type TabFilter = "All" | InvoiceStatus;
const tabFilters: { label: string; value: TabFilter }[] = [
    { label: "All", value: "All" },
    { label: "Pending Payment", value: "PendingPayment" },
    { label: "Paid", value: "Paid" },
    { label: "Cancelled", value: "Cancelled" }
];

// ── Skeleton ──────────────────────────────────────────────────────────────────

function Skeleton({ className }: { className?: string }) {
    return <div className={cn("animate-pulse rounded bg-muted", className)} />;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function InvoicesPage() {
    const [activeTab, setActiveTab] = useState<TabFilter>("All");
    const [search, setSearch] = useState("");
    const { data: allInvoices, isLoading, error } = useInvoices();
    const markPaidMutation = useMarkInvoicePaid();

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (allInvoices ?? []).filter((inv) => {
            if (activeTab !== "All" && inv.status !== activeTab) return false;
            if (q.length === 0) return true;
            return inv.invoiceNumber.toLowerCase().includes(q)
                || inv.tenantName.toLowerCase().includes(q);
        });
    }, [allInvoices, activeTab, search]);

    const countByStatus = (status: InvoiceStatus) =>
        (allInvoices ?? []).filter((inv) => inv.status === status).length;

    const pendingTotal = (allInvoices ?? [])
        .filter((inv) => inv.status === "PendingPayment")
        .reduce((sum, inv) => sum + inv.amount, 0);

    const pendingCount = countByStatus("PendingPayment");

    function handleMarkPaid(id: string) {
        markPaidMutation.mutate(id, {
            onSuccess: () => alert("Invoice marked as paid."),
            onError: (err) => alert(`Failed: ${err instanceof Error ? err.message : "Unknown error"}`)
        });
    }

    return (
        <div className="space-y-4">
            <h1 className="text-xl font-semibold">Invoices</h1>

            {/* Summary card */}
            {pendingCount > 0 && (
                <div className="flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/20">
                    <Receipt className="h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                        <span className="font-semibold">{pendingCount} invoice{pendingCount !== 1 ? "s" : ""}</span> awaiting payment
                        &nbsp;—&nbsp;total outstanding: <span className="font-semibold">{formatCurrency(pendingTotal)}</span>
                    </p>
                </div>
            )}

            {error && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    Failed to load invoices: {error instanceof Error ? error.message : "Unknown error"}
                </div>
            )}

            {/* Search input */}
            <div className="relative max-w-sm">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filter by invoice # or tenant"
                    aria-label="Filter invoices"
                    className="h-8 pl-7 text-sm"
                />
            </div>

            {/* Filter tabs */}
            <div className="flex flex-wrap gap-1 border-b pb-2">
                {tabFilters.map(({ label, value }) => {
                    const count = value === "All"
                        ? (allInvoices ?? []).length
                        : countByStatus(value as InvoiceStatus);
                    return (
                        <button
                            key={value}
                            type="button"
                            onClick={() => setActiveTab(value)}
                            className={cn(
                                "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                                activeTab === value
                                    ? "bg-primary/10 text-primary"
                                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                            )}
                        >
                            {label}
                            <span className={cn(
                                "rounded-full px-1.5 py-0.5 text-xs",
                                activeTab === value ? "bg-primary/20" : "bg-muted"
                            )}>
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Table */}
            <div className="rounded-lg border bg-card shadow-sm">
                {isLoading ? (
                    <div className="space-y-2 p-4">
                        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-muted/40 text-left text-xs font-medium text-muted-foreground">
                                    <th className="px-4 py-2.5">Invoice #</th>
                                    <th className="px-4 py-2.5">Tenant</th>
                                    <th className="px-4 py-2.5">Amount</th>
                                    <th className="px-4 py-2.5">Currency</th>
                                    <th className="px-4 py-2.5">Status</th>
                                    <th className="px-4 py-2.5">Due Date</th>
                                    <th className="px-4 py-2.5">Paid At</th>
                                    <th className="px-4 py-2.5">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                                            {activeTab !== "All" ? "No invoices in this category" : "No invoices yet"}
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((inv) => (
                                        <tr key={inv.id} className="border-b last:border-0 hover:bg-muted/20">
                                            <td className="px-4 py-2.5 font-mono text-xs">{inv.invoiceNumber}</td>
                                            <td className="px-4 py-2.5">
                                                <Link
                                                    to={`/tenants/${inv.tenantId}`}
                                                    className="text-primary hover:underline"
                                                >
                                                    {inv.tenantName}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-2.5 tabular-nums">
                                                {formatCurrency(inv.amount, inv.currency)}
                                            </td>
                                            <td className="px-4 py-2.5 uppercase">{inv.currency}</td>
                                            <td className="px-4 py-2.5"><InvoiceStatusBadge status={inv.status} /></td>
                                            <td className="px-4 py-2.5 text-muted-foreground">{formatDate(inv.dueDate)}</td>
                                            <td className="px-4 py-2.5 text-muted-foreground">{formatDate(inv.paidAt)}</td>
                                            <td className="px-4 py-2.5">
                                                {inv.status === "PendingPayment" && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleMarkPaid(inv.id)}
                                                        disabled={markPaidMutation.isPending}
                                                    >
                                                        {markPaidMutation.isPending
                                                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                            : "Mark Paid"}
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
